import Pagination from 'helpers/Coveo/Pagination';
import QuerySummary from 'helpers/Coveo/QuerySummary';
import Sort from 'helpers/Coveo/SortDropdown';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { JSX, useEffect, useState } from 'react';
import { buildInteractiveResult, buildSearchBox, InteractiveResult, Result } from '@coveo/headless';
import { buildFacet, Facet, FacetValue } from '@coveo/headless';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import BlogSearchHeader, { BlogSearchHeaderProps } from './BlogSearchHeader';
import AppliedFacetFilters from 'helpers/Coveo/AppliedFacetFilters';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import FacetGroup from 'helpers/Coveo/FacetGroup';
import { SvgIcon } from 'helpers/SvgIcon';
import classNames from 'classnames';
import useDictionary from 'lib/hooks/useDictionary';
import UrlManagerController from 'helpers/Coveo/UrlManagerController';
import NoResultsMessage from 'helpers/Coveo/NoResultsMessage';
import { formatDate } from 'lib/utils/utilityFunction';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import { useCoveoSearchSubscription } from 'lib/hooks/useCoveoSearchSubscription';

const BlogSearch = (props: BlogSearchHeaderProps): JSX.Element => {
  const siteSettings = useSiteSettings()?.fields;
  const shouldRedirect = false;

  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });

  useEffect(() => {
    setCoveoConfig({
      searchHub: siteSettings?.blogSRSearchHub?.value || '',
      pipeline: siteSettings?.blogSRPipeline?.value || '',
      accessToken: siteSettings?.blogSRAccessToken?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [
    siteSettings?.blogSRSearchHub?.value,
    siteSettings?.blogSRPipeline?.value,
    siteSettings?.blogSRAccessToken?.value,
  ]);

  if (!coveoConfig?.organizationId || !coveoConfig?.accessToken || !coveoConfig?.pipeline) {
    return <></>;
  }

  return (
    <>
      <CoveoEngineProvider config={coveoConfig}>
        <BlogSearchHeader {...props} shouldRedirect={shouldRedirect} />
        <BlogSearchResultList />
        <UrlManagerController />
      </CoveoEngineProvider>
    </>
  );
};

export default BlogSearch;

export const BlogSearchResultList = (): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { getDictionaryValue } = useDictionary();
  const engine = useCoveoEngine();
  const { ContextResults, firstSearchExecuted } = useCoveoSearchSubscription();

  const fieldsToInclude = [
    'cwblogcontenttype',
    'cwtitle',
    'cwblogdate',
    'cwdescription',
    'cwauthorname',
    'cwauthortitle',
    'cwheroimage',
    'cwblogdate',
  ];

  const [contentTypeFacet, setContentTypeFacet] = useState<Facet | null>(null);
  const [authorNameFacet, setAuthorNameFacet] = useState<Facet | null>(null);

  useEffect(() => {
    if (!engine) return;
    const facet = buildFacet(engine, {
      options: {
        field: 'cwblogcontenttype',
        facetId: 'cwblogcontenttype',
        numberOfValues: 10,
      },
    });
    const authorNamefacet = buildFacet(engine, {
      options: {
        field: 'cwauthorname',
        facetId: 'cwauthorname',
        numberOfValues: 10,
      },
    });
    buildFacet(engine, {
      options: {
        field: 'cwcategory',
        facetId: 'cwcategory',
        numberOfValues: 10,
      },
    });
    buildFacet(engine, {
      options: {
        field: 'cwagegroup',
        facetId: 'cwagegroup',
        numberOfValues: 10,
      },
    });

    buildSearchBox(engine, { options: { numberOfSuggestions: 5 } });

    setContentTypeFacet(facet);
    setAuthorNameFacet(authorNamefacet);
  }, [engine]);

  const resultsToRender = ContextResults?.results || [];

  const handleContentTypeClick = (contentType: string) => {
    if (!contentTypeFacet) return;

    const selected = contentTypeFacet.state.values.find((v: FacetValue) => v.value === contentType);
    if (selected && !selected.state) {
      contentTypeFacet.toggleSelect(selected);
    } else {
      contentTypeFacet.toggleSelect({ value: contentType, numberOfResults: 0, state: 'idle' });
    }
  };

  const handleAuthorNameClick = (authorName: string) => {
    if (!authorNameFacet) return;

    const selected = authorNameFacet.state.values.find((v: FacetValue) => v.value === authorName);
    if (selected && !selected.state) {
      authorNameFacet.toggleSelect(selected);
    } else {
      authorNameFacet.toggleSelect({ value: authorName, numberOfResults: 0, state: 'idle' });
    }
  };

  // stop scroll on drawer open in mobile
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  return (
    <ComponentWrapper className="bg-white">
      <div className="lg:w-[800px] px-10 lg:p-0 max-w-screen-xxl mx-auto coveo-search-list">
        <div className="facet-wrapper lg:block hidden">
          <FacetGroup
            fields={['cwblogcontenttype', 'cwcategory', 'cwagegroup']}
            labels={{
              cwblogcontenttype: 'Content Type',
              cwcategory: 'Topic',
              cwagegroup: 'Age/Life Stage',
            }}
          />
        </div>
        {/* Facet Drawer for Mobile */}
        {isDrawerOpen && (
          <div
            className={classNames(
              'fixed inset-0 z-50 bg-white transform transition-transform duration-500 ease-in-out flex flex-col lg:hidden',
              {
                'translate-x-0': isDrawerOpen,
                'translate-x-full': !isDrawerOpen,
              }
            )}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-10 py-[25px] border-b border-gray-200">
              <div className="text-20 font-bold leading-[28px] font-centuryGothic">
                {getDictionaryValue('SetFilters')}
              </div>
              <button onClick={() => setIsDrawerOpen(false)} aria-label="Close">
                <SvgIcon icon="close" size="lg" className="w-10 h-10 [&>path]:fill-blue-base" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-grow overflow-y-auto px-10">
              <FacetGroup
                fields={['cwblogcontenttype', 'cwcategory', 'cwagegroup']}
                labels={{
                  cwblogcontenttype: 'Content Type',
                  cwcategory: 'Topic',
                  cwagegroup: 'Age/Life Stage',
                }}
              />
            </div>

            {/* Footer Button */}
            <div className="p-10 border-t border-gray-200">
              <ButtonWrapper
                ctaVariant="primary"
                onClick={() => setIsDrawerOpen(false)}
                className="w-full bg-blue-base text-white py-4 rounded-md text-18 font-semibold"
              >
                {getDictionaryValue('ViewResults')}
              </ButtonWrapper>
            </div>
          </div>
        )}
        {/* Mobile only filter open button */}
        <div className="lg:hidden mb-4">
          <ButtonWrapper
            ctaVariant="secondary"
            text="Set Filters"
            ctaIcon="funnel"
            onClick={() => setIsDrawerOpen(true)}
            className="w-full [&>svg]:mt-[-5px] gap-x-4"
            ctaIconAlignment="left"
          ></ButtonWrapper>
        </div>
        <div className="py-6">
          <AppliedFacetFilters
            fields={['cwblogcontenttype', 'cwcategory', 'cwagegroup', 'cwauthorname']}
            labels={{
              cwblogcontenttype: 'Content Type',
              cwcategory: 'Topic',
              cwagegroup: 'Age/Life Stage',
              cwauthorname: 'Author',
            }}
          />
        </div>
        <div className="flex justify-between items-center py-6 w-full flex-wrap">
          {/* Total Count of results */}
          <QuerySummary />
          {/* Sort control lets you select criteria to apply to search results */}
          <Sort
            fieldsToInclude={fieldsToInclude}
            enableNewestSort={true}
            //newestSortField="cwblogdate"
            newestSortField="cwblogdate"
            setNewestAsDefault={true}
          />
        </div>
        <div className="resultlist-wrapper">
          {firstSearchExecuted ? (
            resultsToRender?.length > 0 ? (
              <div className="">
                <div>
                  <section>
                    {resultsToRender.map((item: Result, index) => {
                      const controller: InteractiveResult | null = engine
                        ? buildInteractiveResult(engine, {
                            options: { result: item },
                          })
                        : null;

                      return (
                        <article
                          role="region"
                          aria-labelledby={`${item?.raw?.cwtitle}`}
                          key={index}
                          className="lg:flex-row flex-col-reverse flex py-[30px] border-b border-b-gray-50 last:border-0 lg:gap-[46px] gap-[24px] justify-between"
                        >
                          {/* content section */}
                          <div className="w-full min-w-0">
                            <div className="flex w-full justify-between">
                              <button
                                onClick={() =>
                                  handleContentTypeClick(item?.raw?.cwblogcontenttype as string)
                                }
                                aria-label={`${item?.raw?.cwblogcontenttype} blog content type`}
                              >
                                <PlainTextWrapper
                                  field={{ value: item?.raw?.cwblogcontenttype as string }}
                                  tag="p"
                                  className="text-blue-base text-14 leading-[24px] font-bold font-centuryGothic pb-2 hover:text-darkblue-base cursor-pointer"
                                />
                              </button>
                              <PlainTextWrapper
                                field={{ value: formatDate(item?.raw?.cwblogdate as number) }}
                                tag="p"
                                className="text-14 leading-[24px] font-nunitoSans text-gray-900"
                                aria-label={`Published on ${formatDate(item?.raw?.cwblogdate as number)}`}
                              />
                            </div>
                            <a
                              aria-label={`${item?.raw?.cwtitle} blog title`}
                              className="text-blue-base text-28 leading-31 font-bold font-centuryGothic block py-6 hover:underline hover:text-darkblue-base cursor-pointer w-full"
                              href={item?.clickUri}
                              onClick={() => controller?.select()}
                              onMouseDown={() => controller?.select()}
                            >
                              <PlainTextWrapper
                                field={{ value: item?.raw?.cwtitle as string }}
                                tag="h3"
                                className="w-full block"
                              />
                            </a>
                            {item?.raw?.cwdescription !== undefined && (
                              <div className="text-gray-900 font-nunitoSans text-16 leading-25 font-[500] overflow-hidden line-clamp-2 max-w-full">
                                <RichTextWrapper
                                  field={{
                                    value:
                                      (item?.raw?.cwdescription as string)?.substring(0, 200) +
                                      ((item?.raw?.cwdescription as string)?.length > 200
                                        ? '...'
                                        : ''),
                                  }}
                                  className="overflow-hidden"
                                />
                              </div>
                            )}
                            <div className="flex gap-[8px] pt-6 pb-2 leading-[24px] text-14 text-[#111] font-nunitoSans">
                              <button
                                onClick={() =>
                                  handleAuthorNameClick(item?.raw?.cwauthorname as string)
                                }
                                aria-label={`See more blog posts by ${item?.raw?.cwauthorname}`}
                                className="font-bold !text-blue-base hover:underline cursor-pointer"
                              >
                                <PlainTextWrapper
                                  field={{ value: item?.raw?.cwauthorname as string }}
                                />
                              </button>

                              {/* {item?.raw?.cwauthortitle !== undefined &&
                                item?.raw?.cwauthorname !== undefined && <span>|</span>} */}
                              {[item?.raw?.cwauthortitle, item?.raw?.cwauthorname].every(
                                (v) => typeof v === 'string' && v.trim() !== ''
                              ) && <span>|</span>}
                              <PlainTextWrapper
                                field={{ value: item?.raw?.cwauthortitle as string }}
                              />
                            </div>
                          </div>
                          {/* Image section */}
                          <div className="lg:max-w-[298px]">
                            <ImageWrapper
                              field={{
                                value: {
                                  src: item?.raw?.cwheroimage as string,
                                  alt: item?.raw?.cwtitle as string,
                                },
                              }}
                              className="w-full rounded-[8px]"
                            />
                          </div>
                        </article>
                      );
                    })}
                  </section>
                </div>
              </div>
            ) : (
              <>
                <NoResultsMessage />
              </>
            )
          ) : null}
        </div>

        <div className="pagination-wrapper">
          <Pagination fieldsToInclude={fieldsToInclude} className="justify-end mt-12" />
        </div>
      </div>
    </ComponentWrapper>
  );
};
